import React, { useState, useRef, useMemo, useEffect } from "react";
import { useParams, useHistory, Switch } from "react-router-dom";
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography, Box } from "@curves/ui-base";
import useDocHealthDeclarationApi from "@apis/useDocHealthDeclarationApi";
import HealthSurveyContent from "@icoach/documents/healthDeclaration/HealthSurveyContent";
import HealthDeclarationHistory from "@icoach/documents/healthDeclaration/HealthDeclarationHistory";
import { DOC_HEALTH_DECLARATION_LINK } from "@icoach/documents/membership/staticData";
import { SearchBar, BlankFormBox } from "@icoach/documents/components/";
import { ConfirmTemporaryContractDialog, ConfirmHealthDeclarationContinueDialog } from "@icoach/documents/dialog/";
import { CLUB_DOCUMENTS_ROUTER, CLUB_INBODY_ROUTER, CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import useToggleDialog from "@util/hook/useToggleDialog";
import { isEqual } from "@util/utils";
import { TRANSFERIN_ENTEREDTYPE, PEOPLE_TYPE, QUERY_KEYS, IN_BODY_INTERVIEW_TABS, MEMBERS_MEMBER_DETAIL_TABS } from "@icoach/constants/";

const MEMBERSHIPPEOPLETYPES = [PEOPLE_TYPE.CUSTOMER, PEOPLE_TYPE.MEMBER, PEOPLE_TYPE.INVALIDMEMBER, PEOPLE_TYPE.TRANSFERMEMBER, PEOPLE_TYPE.ONLYJOIN];
const TRANSFERINPEOPLETYPES = [PEOPLE_TYPE.TRANSFERMANUALLY, PEOPLE_TYPE.TRANSFERINMEMBER];

const DIALOGTYPEMAP = {
	TEMPORARY: "temporary",
	HEALTHDECLARATIONCONTINUE: "healthDeclarationContinue",
};

const HealthDeclarationPage = props => {
	const { getInitHealthDeclarationOptionApi, getDocHealthDeclarationTemporaryApi, deleteDocHealthDeclarationTemporaryApi } = useDocHealthDeclarationApi();
	const { peopleType: peopleTypeProps, referenceID: referenceIDProps } = useParams();
	const history = useHistory();
	const [options, setOptions] = useState({});
	const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
	const [isMounted, setMounted] = useState(false);
	const searchRef = useRef(null);
	const temporaryIDRef = useRef(0);
	const contractIDRef = useRef(0);
	const queryString = window.location.search;

	const checkPeopleType = opts => {
		const { targetOptions = [] } = opts;
		const isInclude = targetOptions.find(({ value }) => isEqual(peopleTypeProps, value));
		const peopleType = PEOPLE_TYPE.MEMBER;
		if (!peopleTypeProps || !isInclude) {
			history.replace(CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_PEOPLETYPE_PATH(peopleType));
		}
	};

	// 重設 按鈕
	const handleResetClick = peopleType => {
		const path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_PEOPLETYPE_PATH(peopleType);
		history.replace(path);
	};

	// 搜尋 按鈕
	const handleSearchHistoryClick = id => {
		const { targetID: referenceID, targetType: peopleType } = searchRef.current.getResult();
		const path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_HISTORY_PATH(peopleType, referenceID);
		history.replace(path);
	};

	// 按鈕 新增
	const handleCreateClick = () => {
		const { targetID: id, targetType: type } = searchRef.current.getResult();
		const params = { id, type };
		doCreateBlankContractApi(params, type, id);
	};

	// 彈出視窗 要刪除暫存資料
	const handleDialogNoClick = () => {
		const params = {
			id: referenceIDProps,
			type: peopleTypeProps,
		};
		doDeleteTemporaryDataApi(params);
	};

	// 彈出視窗 讀取上次暫存資料繼續編輯
	const handleDialogYseClick = () => {
		if (!temporaryIDRef.current) return;
		const contractID = temporaryIDRef.current;
		const path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_CONTRACT_PATH(peopleTypeProps, referenceIDProps, contractID);
		handleCloseDialog();
		history.push(path);
	};

	const handleReturnContinueDoc = id => {
		if (id > 0) contractIDRef.current = id;
		handleOpenDialog(DIALOGTYPEMAP.HEALTHDECLARATIONCONTINUE);
	};

	function getHealthDeclarationRedirect(currentQuery, peopleType, referenceID, contractID) {
		let redirectPath = "";
		let confirmationMessage = "";

		if (currentQuery.indexOf(QUERY_KEYS.IN_BODY_INTERVIEW) !== -1) {
			redirectPath = CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.SUMMARY, referenceID);
			confirmationMessage = "已填寫健康申告書，是否返回「開始面談」畫面?";
		} else if (currentQuery.indexOf(QUERY_KEYS.IN_BODY_INTERVIEW) !== -1) {
			redirectPath = CLUB_MEMBERS_ROUTER.MEMBER_DETAIL.GET_TABS_PATH(referenceID, MEMBERS_MEMBER_DETAIL_TABS.WORKOUT);
			confirmationMessage = "已填寫健康申告書，是否返回會員頁?";
		} else if (isEqual(peopleType, PEOPLE_TYPE.ONLYJOIN) && contractID > 0) {
			// 特殊邏輯判斷 合約當作ID才能找到該會員
			redirectPath = CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.GET_CONTRACT_PATH(peopleType, contractID, 0);
			confirmationMessage = "已填寫健康申告書，是否繼續填寫入會申請書?";
		} else if (isEqual(peopleType, PEOPLE_TYPE.TRANSFERMANUALLY) && contractID > 0) {
			// 特殊邏輯判斷 合約當作ID才能找到該會員
			redirectPath = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_CONTRACT_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED, peopleType, contractID, 0);
			confirmationMessage = "已填寫健康申告書，是否繼續填寫移入申請書?";
		} else if (MEMBERSHIPPEOPLETYPES.some(t => isEqual(t, peopleType))) {
			redirectPath = CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.GET_CONTRACT_PATH(peopleType, referenceID, 0);
			confirmationMessage = "已填寫健康申告書，是否繼續填寫入會申請書?";
		} else if (TRANSFERINPEOPLETYPES.some(t => isEqual(t, peopleType))) {
			redirectPath = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_CONTRACT_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED, peopleType, referenceID, 0);
			confirmationMessage = "已填寫健康申告書，是否繼續填寫移入申請書?";
		}

		return { redirectPath, confirmationMessage };
	}

	const handleDialogContinueYseClick = () => {
		history.replace(getHealthDeclarationRedirect(queryString, peopleTypeProps, referenceIDProps, contractIDRef.current).redirectPath);
	};

	// 新增空白頁面
	const doCreateBlankContractApi = async (params, peopleType, referenceID) => {
		temporaryIDRef.current = 0;
		const temporaryID = await getDocHealthDeclarationTemporaryApi(params);
		if (temporaryID !== 0 && !!temporaryID) {
			temporaryIDRef.current = temporaryID;
			handleOpenDialog(DIALOGTYPEMAP.TEMPORARY);
		} else {
			const path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_CONTRACT_PATH(peopleType, referenceID, 0);
			history.push(path);
		}
	};

	// 刪除暫存合約資料
	const doDeleteTemporaryDataApi = async parmas => {
		const resp = await deleteDocHealthDeclarationTemporaryApi(parmas);
		if (resp) {
			const contractID = 0;
			const path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_CONTRACT_PATH(peopleTypeProps, referenceIDProps, contractID);
			handleCloseDialog();
			history.push(path);
		}
	};

	const initOptionDataApi = async () => {
		const opts = await getInitHealthDeclarationOptionApi();
		if (opts) {
			checkPeopleType(opts);
			setOptions(opts);
		}
		setMounted(true);
	};

	const useSearchBarMemo = useMemo(
		() => {
			return (
				<SearchBar
					ref={searchRef}
					className="mb-4"
					skipPeopleOptions={[PEOPLE_TYPE.ONLYJOIN, PEOPLE_TYPE.TRANSFERMANUALLY]}
					notSearchButtonOptions={[PEOPLE_TYPE.ONLYJOIN, PEOPLE_TYPE.TRANSFERMANUALLY]}
					sourceOption={options.targetOptions}
					onResetEvent={handleResetClick}
					onCreateEvent={handleCreateClick}
					onSearchHistoryEvent={handleSearchHistoryClick}
					routerPath={CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.BASE}
				/>
			);
		},
		// eslint-disable-next-line
		[options]
	);

	useEffect(
		() => {
			initOptionDataApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Box className="container main-container-spacing doc-health-declaration">
			{isMounted && (
				<React.Fragment>
					<Box component="header" className="page-header">
						<Typography className="page-header-title" variant="h3">
							健康狀況申告書
						</Typography>
						<BlankFormBox sourceData={DOC_HEALTH_DECLARATION_LINK} />
					</Box>
					{useSearchBarMemo}
					<Switch>
						<PrivateRoute
							exact
							path={CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.HISTORY}
							as={HealthDeclarationHistory}
							onBlankContractClick={handleCreateClick}
							onRefresh={handleSearchHistoryClick}
						/>
						<PrivateRoute
							exact
							path={CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.CONTRACT}
							as={HealthSurveyContent}
							onReturnHistory={handleSearchHistoryClick}
							onReturnContinueDoc={handleReturnContinueDoc}
						/>
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.READONLY_CONTRACT} as={HealthSurveyContent} isReadOnly={true} onReturnHistory={handleSearchHistoryClick} />
					</Switch>
				</React.Fragment>
			)}
			<ConfirmTemporaryContractDialog
				open={isEqual(openTarget, DIALOGTYPEMAP.TEMPORARY)}
				handleClose={handleCloseDialog}
				handleClickNo={handleDialogNoClick}
				handleClickYes={handleDialogYseClick}
			/>
			<ConfirmHealthDeclarationContinueDialog
				open={isEqual(openTarget, DIALOGTYPEMAP.HEALTHDECLARATIONCONTINUE)}
				nextDocTitle={getHealthDeclarationRedirect(queryString, peopleTypeProps, referenceIDProps, contractIDRef.current).confirmationMessage}
				handleClose={handleCloseDialog}
				handleClickNo={handleCloseDialog}
				handleClickYes={handleDialogContinueYseClick}
			/>
		</Box>
	);
};

export default HealthDeclarationPage;
